import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TextField } from "react-aria-components";
import { Pencil, Trash2 } from "lucide-react";
import type { PmPerson } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { initials } from "@/lib/initials";
import { formatDateTime } from "./pm-labels.js";
import { useInstituteLabel } from "./pm-institutes.js";

export interface PmNoteData {
  id: number;
  text: string;
  author: PmPerson;
  createdAt: string;
  editedAt: string | null;
}

/**
 * One note — on a Drehbuch step or on a task, the same rules (D2, owner 2026-10-28): the author edits their own, the
 * author or a PM admin deletes (after a confirmation; the text stays in the history). "bearbeitet" marks an edited
 * note. The caller passes the writes, so step and task notes share this without sharing an endpoint.
 */
export function PmNote({
  note,
  canEdit,
  canDelete,
  onUpdate,
  onDelete,
}: {
  note: PmNoteData;
  canEdit: boolean;
  canDelete: boolean;
  onUpdate: (text: string) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
}) {
  const { t, i18n } = useTranslation();
  const instituteLabel = useInstituteLabel();
  const [editing, setEditing] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState<"save" | "delete" | null>(null);

  const run = (fn: () => Promise<unknown>, kind: "save" | "delete", done: () => void) => {
    setPending(true);
    setFailed(null);
    fn().then(done, () => setFailed(kind)).finally(() => setPending(false));
  };

  return (
    <div className="flex gap-2.5">
      <span aria-hidden="true" className="bg-muted inline-flex size-7 shrink-0 items-center justify-center rounded-full text-[0.7rem] font-semibold">
        {initials(note.author.name)}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[0.8rem] font-semibold">{note.author.name}</span>
          <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs">{instituteLabel(note.author)}</span>
          <span className="text-muted-foreground text-xs">
            {formatDateTime(note.createdAt, i18n.language)}
            {note.editedAt ? ` · ${t("pmBoard.note.edited")}` : ""}
          </span>
          <span className="ml-auto flex gap-1">
            {canEdit && editing === null ? (
              <Button variant="ghost" size="icon-sm" aria-label={t("pmBoard.note.edit")} onPress={() => setEditing(note.text)}>
                <Pencil aria-hidden="true" />
              </Button>
            ) : null}
            {canDelete && editing === null ? (
              <Button variant="ghost" size="icon-sm" aria-label={t("pmBoard.note.delete")} onPress={() => setConfirmDelete(true)}>
                <Trash2 aria-hidden="true" />
              </Button>
            ) : null}
          </span>
        </div>
        {editing === null ? (
          <p className="m-0 text-sm break-words whitespace-pre-wrap">{note.text}</p>
        ) : (
          <div className="flex flex-col gap-2">
            <TextField value={editing} onChange={setEditing} aria-label={t("pmBoard.note.editLabel")}>
              <Textarea rows={3} maxLength={10_000} autoFocus />
            </TextField>
            {failed === "save" ? (
              <p role="alert" className="text-destructive m-0 text-sm">
                {t("pmBoard.note.saveFailed")}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onPress={() => setEditing(null)} isDisabled={pending}>
                {t("pmBoard.note.cancel")}
              </Button>
              <Button variant="default" isDisabled={pending || !editing.trim()} onPress={() => run(() => onUpdate(editing.trim()), "save", () => setEditing(null))}>
                {t("pmBoard.note.save")}
              </Button>
            </div>
          </div>
        )}
      </div>
      <ConfirmDialog
        isOpen={confirmDelete}
        title={t("pmBoard.note.deleteTitle")}
        message={
          <>
            {t("pmBoard.note.deleteBody")}
            {failed === "delete" ? (
              <span role="alert" className="text-destructive mt-2 block">
                {t("pmBoard.note.deleteFailed")}
              </span>
            ) : null}
          </>
        }
        confirmLabel={t("pmBoard.note.delete")}
        destructive
        isPending={pending}
        onConfirm={() => run(onDelete, "delete", () => setConfirmDelete(false))}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
